import { useState, useEffect, useReducer } from 'react';
import { createPortal } from 'react-dom';
import { Slider } from '@mui/material';
import { ChevronDown, ChevronRight } from 'react-feather';
import ActionModal from '../../../../../components/action-modal';
import {
  chatAgentModalProps,
  ChatAgentConfig,
  ChatAgentValidationErrors,
} from './chat-agent-modal.props';
import { initialChatAgentState, chatAgentModalReducer } from './chat-agent-modal.reducer';

import { truncateData, validateTextField } from '../../../../../services';
import makeToast from '../../../../../components/toaster';
import { FilterDropdown, InputField } from '../../../../../components';
import {
  createChatAgent,
  updateAgent,
  GetAllMcpChatService,
  getAllKnowledgeBaseFolders,
  EditMcpChatServce,
} from '../../services/chat';
import { getMcpToolName, getMcpToolNames } from '../../lib/mcp-tool-utils';
import { WebSearchIcon, ActiveSchool, botIcon, Plus } from '../../../../../assets/images';
import TuneModal from '../tune-modal';
import { EMPTY_MCP_RE_AUTH } from '../tune-modal/tune-modal.props';
import StyledTooltip from '../../../../../components/tooltip';

function ChatAgentModal({
  onClose,
  open,
  llmPermissions,
  editMode = false,
  hideTopActionButtons = false,
  agentData,
  onSave,
}: chatAgentModalProps) {
  const [state, dispatch] = useReducer(chatAgentModalReducer, initialChatAgentState);
  const [mcpServices, setMcpServices] = useState<
    {
      id: number;
      name: string;
      tools: string[];
      disabled_tools: string[];
      status: string;
      url: string;
    }[]
  >([]);
  const [knowledgeBases, setKnowledgeBases] = useState<
    {
      id: number;
      name: string;
      type: string;
    }[]
  >([]);
  const [showTuneModal, setShowTuneModal] = useState<boolean>(false);
  const [models, setModels] = useState<
    { id: number | string; name: string; proverID: number | string; provider_name: string }[]
  >([]);

  useEffect(() => {
    if (open) {
      setModels(llmPermissions ?? []);
    }
  }, [open, llmPermissions]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [mcpResponse, kbResponse] = await Promise.all([
          GetAllMcpChatService(),
          getAllKnowledgeBaseFolders(),
        ]);

        if (mcpResponse.status === 200) {
          const mcpData = mcpResponse?.data?.data || [];
          setMcpServices(mcpData);
        }

        if (kbResponse.status === 200) {
          const kbData = kbResponse?.data?.data || [];
          setKnowledgeBases(kbData);
        }
      } catch (error) {
        makeToast('error', 'Failed to load data');
      }
    };
    if (open) {
      fetchData();
      // Prefill form in edit mode
      if (editMode && agentData) {
        dispatch({
          type: 'SET_CONFIG',
          payload: {
            name: agentData.agent_name || '',
            description: agentData.description || '',
            selectedModel: agentData.model_id ? String(agentData.model_id) : '',
            temperature: parseFloat(agentData.temperature as string) || 0.7,
            maxTokens: agentData.max_tokens || 1000,
            systemInstructions: agentData.system_prompt || '',
            kbAccess: Boolean(agentData.knowledge_base_enabled),
            codeInterpreterAccess: Boolean(agentData.code_interpreter_enabled),
            webSearchAccess: Boolean(agentData.web_search_enabled),
            mcpAccess: Boolean(agentData.mcp_enabled),
          },
        });
        // Set selected KB and MCP IDs
        if (agentData.knowledge_base_id) {
          dispatch({ type: 'SET_SELECTED_KB_ID', payload: agentData.knowledge_base_id });
        }
        if (agentData.mcp_id) {
          // Handle both single number and array for backward compatibility
          const mcpIds = Array.isArray(agentData.mcp_id) ? agentData.mcp_id : [agentData.mcp_id];
          dispatch({ type: 'SET_SELECTED_MCP_IDS', payload: mcpIds });
        }
      }
    }
  }, [open, showTuneModal, editMode, agentData]);

  const onChange = async (field: keyof ChatAgentConfig, value: string | number | boolean) => {
    dispatch({ type: 'SET_CONFIG', payload: { [field]: value } });

    // When Knowledge Base Access is turned on, select the first KB
    if (field === 'kbAccess' && value === true && knowledgeBases.length > 0) {
      dispatch({ type: 'SET_SELECTED_KB_ID', payload: knowledgeBases[0].id });
    }

    // When Knowledge Base Access is turned off, deselect the KB
    if (field === 'kbAccess' && value === false) {
      dispatch({ type: 'SET_SELECTED_KB_ID', payload: null });
    }

    // When MCP Access is turned on, select ALL MCPs and enable all their tools
    if (field === 'mcpAccess' && value === true) {
      // Select all MCPs
      const allMcpIds = mcpServices.map((mcp) => mcp.id);
      dispatch({ type: 'SET_SELECTED_MCP_IDS', payload: allMcpIds });

      // Enable all tools for ALL MCPs
      const mcpPromises = mcpServices.map(async (mcp) => {
        if (mcp.tools && mcp.tools.length > 0) {
          const hasDisabledTools = mcp.disabled_tools && mcp.disabled_tools.length > 0;
          if (hasDisabledTools) {
            // Optimistic update: Update local state immediately
            setMcpServices((prevServices) =>
              prevServices.map((service) =>
                service.id === mcp.id
                  ? {
                      ...service,
                      disabled_tools: [],
                    }
                  : service,
              ),
            );

            // Make API call in the background
            const payload = {
              status: mcp.status,
              disabled_tools: [],
            };
            try {
              await EditMcpChatServce(payload, mcp.id);
            } catch (error) {
              // Revert optimistic update on error
              setMcpServices((prevServices) =>
                prevServices.map((service) =>
                  service.id === mcp.id
                    ? {
                        ...service,
                        disabled_tools: mcp.disabled_tools || [],
                      }
                    : service,
                ),
              );
            }
          }
        }
      });

      // Wait for all API calls to complete, then refresh
      try {
        await Promise.all(mcpPromises);
        const mcpResponse = await GetAllMcpChatService();
        if (mcpResponse.status === 200) {
          const mcpData = mcpResponse?.data?.data || [];
          setMcpServices(mcpData);
        }
      } catch (error) {
        // Error handling is done per MCP above
      }
    }

    // When MCP Access is turned off, deselect all MCPs and disable all their tools
    if (field === 'mcpAccess' && value === false) {
      // Disable all tools for all selected MCPs
      const selectedMcps = mcpServices.filter((mcp) => state.selectedMcpIds.includes(mcp.id));
      const mcpPromises = selectedMcps.map(async (mcp) => {
        if (mcp.tools && mcp.tools.length > 0) {
          // Optimistic update: Update local state immediately - disable all tools
          setMcpServices((prevServices) =>
            prevServices.map((service) =>
              service.id === mcp.id
                ? {
                    ...service,
                    disabled_tools: getMcpToolNames(service.tools),
                  }
                : service,
            ),
          );

          // Make API call in the background
          const payload = {
            status: mcp.status,
            disabled_tools: getMcpToolNames(mcp.tools),
          };
          try {
            await EditMcpChatServce(payload, mcp.id);
          } catch (error) {
            // Revert optimistic update on error
            setMcpServices((prevServices) =>
              prevServices.map((service) =>
                service.id === mcp.id
                  ? {
                      ...service,
                      disabled_tools: mcp.disabled_tools || [],
                    }
                  : service,
              ),
            );
          }
        }
      });

      // Wait for all API calls to complete, then refresh
      try {
        await Promise.all(mcpPromises);
        const mcpResponse = await GetAllMcpChatService();
        if (mcpResponse.status === 200) {
          const mcpData = mcpResponse?.data?.data || [];
          setMcpServices(mcpData);
        }
      } catch (error) {
        // Error handling is done per MCP above
      }

      // Deselect all MCPs
      dispatch({ type: 'SET_SELECTED_MCP_IDS', payload: [] });
    }

    let error = '';
    if (field === 'name' && typeof value === 'string') {
      error = validateTextField(value, 'Name');
    } else if (field === 'description' && typeof value === 'string') {
      error = value ? '' : 'Description is required';
    } else if (field === 'selectedModel' && typeof value === 'string') {
      error = value ? '' : 'Model selection is required';
    } else if (field === 'maxTokens' && typeof value === 'number') {
      error = value > 0 ? '' : 'Max tokens must be greater than 0';
    }

    dispatch({ type: 'SET_ERRORS', payload: { ...state.errors, [field]: error || undefined } });
  };

  const handleSave = async () => {
    const nameError = validateTextField(state.config.name, 'Name');
    const descriptionError = state.config.description ? '' : 'Description is required';
    const modelError = state.config.selectedModel ? '' : 'Model selection is required';
    // const maxTokensError = state.config.maxTokens > 0 ? '' : 'Max tokens must be greater than 0';

    const newErrors: ChatAgentValidationErrors = {};
    if (nameError) newErrors.name = nameError;
    if (descriptionError) newErrors.description = descriptionError;
    if (modelError) newErrors.selectedModel = modelError;
    // if (maxTokensError) newErrors.maxTokens = maxTokensError;

    if (Object.keys(newErrors).length > 0) {
      dispatch({ type: 'SET_ERRORS', payload: newErrors });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const selectedModelData = models.find(
        (model) => String(model.id) === state.config.selectedModel,
      );

      const payload = {
        agent_name: state.config.name,
        description: state.config.description,
        provider_id: selectedModelData?.proverID as number,
        model_id: parseInt(state.config.selectedModel, 10),
        system_prompt: state.config.systemInstructions,
        temperature: state.config.temperature,
        // max_tokens: state.config.maxTokens,
        knowledge_base_enabled: state.config.kbAccess,
        knowledge_base_id:
          state.config.kbAccess && state.selectedKbId ? state.selectedKbId : undefined,
        code_interpreter_enabled: state.config.codeInterpreterAccess,
        web_search_enabled: state.config.webSearchAccess,
        mcp_enabled: state.config.mcpAccess,
        mcp_id:
          state.config.mcpAccess && state.selectedMcpIds.length > 0
            ? state.selectedMcpIds
            : undefined,
      };

      let response;
      if (editMode && agentData?.id) {
        response = await updateAgent(agentData.id, payload);
      } else {
        response = await createChatAgent(payload);
      }

      if (response.status === 201 || response.status === 200) {
        makeToast(
          'success',
          editMode ? 'Agent updated successfully!' : 'Chat agent created successfully!',
        );
        if (onSave) {
          await onSave();
        }
        onClose();
      }
    } catch (error) {
      makeToast('error', editMode ? 'Failed to update agent' : 'Failed to create chat agent');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleUpdateMcpTool = async (
    mcpId: number,
    tool: string,
    currentDisabledTools: string[],
    currentStatus: string,
  ) => {
    const isCurrentlyDisabled = currentDisabledTools.includes(tool);
    const updatedDisabledTools = isCurrentlyDisabled
      ? currentDisabledTools.filter((t) => t !== tool)
      : [...currentDisabledTools, tool];

    // Optimistic update: Update local state immediately
    setMcpServices((prevServices) =>
      prevServices.map((mcp) =>
        mcp.id === mcpId
          ? {
              ...mcp,
              disabled_tools: updatedDisabledTools,
            }
          : mcp,
      ),
    );

    // Make API call in the background
    const payload = {
      status: currentStatus,
      disabled_tools: updatedDisabledTools,
    };

    try {
      const response = await EditMcpChatServce(payload, mcpId);
      if (response.status === 200) {
        // Refresh MCP services to sync with server (optional, for consistency)
        const mcpResponse = await GetAllMcpChatService();
        if (mcpResponse.status === 200) {
          const mcpData = mcpResponse?.data?.data || [];
          setMcpServices(mcpData);
        }
      }
    } catch (error) {
      // Revert optimistic update on error
      setMcpServices((prevServices) =>
        prevServices.map((mcp) =>
          mcp.id === mcpId
            ? {
                ...mcp,
                disabled_tools: currentDisabledTools,
              }
            : mcp,
        ),
      );
      makeToast('error', 'Failed to update tool status');
    }
  };

  const onCloseModal = () => {
    dispatch({ type: 'RESET_STATE' });
    onClose();
  };

  return (
    <ActionModal
      open={open}
      modalTitle={editMode ? 'Edit Chat Agent' : 'Create New Chat Agent'}
      modalDesc="Configure your custom chat agent"
      modalLogo={botIcon}
      resetData={onCloseModal}
      saveData={handleSave}
      confirmButtonText={
        state.isLoading
          ? `${editMode ? 'Updating' : 'Creating'}...`
          : `${editMode ? 'Update' : 'Create'} Agent`
      }
      disableSaveButton={state.isLoading}
      className="w-[500px]"
    >
      <div className="flex flex-col gap-3">
        {!hideTopActionButtons && (
          <div className="flex flex-row gap-3 w-full">
            <button
              type="button"
              onClick={() => {
                dispatch({ type: 'RESET_STATE' });
              }}
              className="flex-1 flex items-center justify-center gap-2 bg-[#0A8BC7] text-white px-4 py-2.5 rounded-lg font-medium text-sm hover:bg-[#247ba4] transition-colors"
            >
              <img src={botIcon} alt="New Agent" className="w-5 h-5" />
              <span>New Chat Agent</span>
            </button>
            <button
              type="button"
              onClick={() => setShowTuneModal(true)}
              className="flex-1 flex items-center justify-center gap-2 bg-[#0A8BC7] text-white px-4 py-2.5 rounded-lg font-medium text-sm hover:bg-[#247ba4] transition-colors"
            >
              <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#F48B00]">
                <img
                  src={Plus}
                  alt="Add MCP"
                  className="w-3 h-3"
                  style={{
                    filter: 'brightness(0) saturate(100%) invert(1)',
                  }}
                />
              </span>
              <span>Add MCP</span>
            </button>
          </div>
        )}

        <InputField
          label="Name"
          placeholder="Enter agent name"
          value={state.config.name}
          handleChange={(e) => onChange('name', e.target.value)}
          error={state.errors.name}
          profileInputStyle
          isRequired
        />

        <div className="flex flex-col text-sm">
          <span className="text-[#6A6A6A] font-medium">
            Description<span className="text-red-600">*</span>
          </span>
          <textarea
            placeholder="Define agent purpose"
            className={`mt-1 rounded-lg px-3 py-2 min-h-[80px] ${
              state.errors.description ? '!border-red-500 border' : 'border border-[#E4E4E7]'
            }`}
            value={state.config.description}
            onChange={(e) => onChange('description', e.target.value)}
          />
          {state.errors.description && (
            <span className="text-red-500 text-xs mt-1">{state.errors.description}</span>
          )}
        </div>

        <div className="flex flex-col text-sm gap-2">
          <span className="text-[#6A6A6A] font-medium">Model Configuration</span>

          <div className="bg-[#e0f1f8] rounded-lg p-4 flex flex-col gap-3">
            <div className="flex flex-col">
              <span className="text-[#6A6A6A] font-medium">
                Select Model<span className="text-red-600">*</span>
              </span>
              <FilterDropdown
                options={(() => {
                  const grouped = models.reduce(
                    (acc, model) => {
                      const pid = String(model.proverID);
                      if (!acc[pid]) {
                        acc[pid] = { providerName: model.provider_name, models: [] };
                      }
                      acc[pid].models.push(model);
                      return acc;
                    },
                    {} as Record<string, { providerName: string; models: typeof models }>,
                  );
                  return Object.values(grouped).flatMap((group) =>
                    group.models.map((model) => ({
                      value: String(model.id),
                      label: `${group.providerName} - ${model.name}`,
                    })),
                  );
                })()}
                value={state.config.selectedModel}
                onChange={(v: string) => onChange('selectedModel', v)}
                placeholder="Select a model"
                showClear={false}
                searchable
                className={`mt-1 border rounded-lg px-3 py-2 text-[#6A6A6A] w-full text-left flex items-center justify-between ${
                  state.errors.selectedModel ? '!border-red-500' : 'border-[#E4E4E7]'
                }`}
              />
              {state.errors.selectedModel && (
                <span className="text-red-500 text-xs mt-1">{state.errors.selectedModel}</span>
              )}
            </div>

            <div className="flex flex-col">
              <span className="text-[#6A6A6A] font-medium mb-1">Temperature</span>
              <div className="border-2 px-3 pt-2 pb-1 rounded-lg bg-white">
                <Slider
                  value={state.config.temperature}
                  onChange={(_, value) => onChange('temperature', value as number)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                />
              </div>
            </div>

            {/* <InputField
              type="number"
              label="Max Tokens"
              value={state.config.maxTokens}
              handleChange={(e) => onChange('maxTokens', parseInt(e.target.value, 10) || 0)}
              error={state.errors.maxTokens}
              profileInputStyle
              min={1}
              isRequired
            /> */}

            <div className="flex flex-col">
              <span className="text-[#6A6A6A] font-medium">System Instructions</span>
              <textarea
                placeholder="Enter system instructions"
                className="mt-1 rounded-lg px-3 py-2 min-h-[80px] border border-[#E4E4E7]"
                value={state.config.systemInstructions}
                onChange={(e) => onChange('systemInstructions', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-[#6A6A6A] font-medium">Access Permissions</span>

          <div className="bg-[#e0f1f8] rounded-lg p-4 flex flex-col gap-3">
            {/* Knowledge Base Access */}
            <div className="border border-gray-200 rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      dispatch({
                        type: 'SET_KB_SECTION_EXPANDED',
                        payload: !state.kbSectionExpanded,
                      })
                    }
                    className="w-4 h-4 flex items-center justify-center bg-transparent border-none p-0 focus:outline-none"
                  >
                    {state.kbSectionExpanded ? (
                      <ChevronDown size={12} className="text-gray-600" />
                    ) : (
                      <ChevronRight size={12} className="text-gray-600" />
                    )}
                  </button>
                  <img src={ActiveSchool} alt="Knowledge Base" className="w-4 h-4" />
                  <span className="text-sm text-[#6A6A6A]">Knowledge Base Access</span>
                </div>
                <label className="switch" htmlFor="kb-checkbox">
                  <span className="sr-only text-[#6A6A6A]">Knowledge Base Access</span>
                  <input
                    id="kb-checkbox"
                    type="checkbox"
                    checked={state.config.kbAccess}
                    onChange={(e) => onChange('kbAccess', e.target.checked)}
                  />
                  <span className="slider round" />
                </label>
              </div>
              {state.kbSectionExpanded && (
                <div className="mt-3 max-h-[200px] overflow-y-auto invisible-scroller">
                  {knowledgeBases?.map((kb) => (
                    <div key={kb.id} className="mb-2">
                      <div className="flex items-center gap-3 px-2 py-1">
                        <input
                          type="radio"
                          name="knowledgeBase"
                          id={`kb-${kb.id}`}
                          checked={state.selectedKbId === kb.id}
                          onChange={() => {
                            dispatch({
                              type: 'SET_SELECTED_KB_ID',
                              payload: kb.id,
                            });
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                          disabled={!state.config.kbAccess}
                        />
                        <label
                          htmlFor={`kb-${kb.id}`}
                          className={`text-sm font-medium cursor-pointer flex-1 ${
                            !state.config.kbAccess ? 'text-gray-400' : 'text-gray-700'
                          }`}
                        >
                          {kb.name} ({kb.type})
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* MCP Access */}
            <div className="border border-gray-200 rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      dispatch({
                        type: 'SET_MCP_SECTION_EXPANDED',
                        payload: !state.mcpSectionExpanded,
                      })
                    }
                    className="w-4 h-4 flex items-center justify-center bg-transparent border-none p-0 focus:outline-none"
                  >
                    {state.mcpSectionExpanded ? (
                      <ChevronDown size={12} className="text-gray-600" />
                    ) : (
                      <ChevronRight size={12} className="text-gray-600" />
                    )}
                  </button>
                  <img
                    src={Plus}
                    alt="MCP"
                    className="w-3 h-3"
                    style={{
                      filter:
                        'brightness(0) saturate(100%) invert(56%) sepia(98%) saturate(1000%) hue-rotate(360deg) brightness(103%) contrast(101%)',
                    }}
                  />
                  <span className="text-sm text-[#6A6A6A]">MCP</span>
                </div>
                <label className="switch" htmlFor="mcp-checkbox">
                  <span className="sr-only text-[#6A6A6A]">MCP</span>
                  <input
                    id="mcp-checkbox"
                    type="checkbox"
                    checked={state.config.mcpAccess}
                    onChange={(e) => onChange('mcpAccess', e.target.checked)}
                  />
                  <span className="slider round" />
                </label>
              </div>
              {state.mcpSectionExpanded && (
                <div className="mt-3 max-h-[200px] overflow-y-auto invisible-scroller">
                  {mcpServices && mcpServices.length > 0 ? (
                    mcpServices.map((mcp) => {
                      const isExpanded = state.expandedMcp === mcp.id;
                      return (
                        <div key={mcp.id} className="mb-2">
                          <div className="flex items-center justify-between px-2 py-1">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  dispatch({
                                    type: 'SET_EXPANDED_MCP',
                                    payload: isExpanded ? null : mcp.id,
                                  })
                                }
                                className="w-4 h-4 flex items-center justify-center bg-transparent border-none p-0 focus:outline-none"
                              >
                                {isExpanded ? (
                                  <ChevronDown size={12} className="text-gray-600" />
                                ) : (
                                  <ChevronRight size={12} className="text-gray-600" />
                                )}
                              </button>
                              <span className="text-sm font-medium">{mcp.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="mcpSwitch">
                                <input
                                  type="checkbox"
                                  checked={state.selectedMcpIds.includes(mcp.id)}
                                  disabled={!state.config.mcpAccess}
                                  onChange={async () => {
                                    // Don't allow changes if MCP Access is off
                                    if (!state.config.mcpAccess) {
                                      return;
                                    }
                                    const isCurrentlySelected = state.selectedMcpIds.includes(
                                      mcp.id,
                                    );
                                    dispatch({
                                      type: 'TOGGLE_MCP_ID',
                                      payload: mcp.id,
                                    });

                                    // When MCP is selected, enable all its tools
                                    if (!isCurrentlySelected && mcp.tools && mcp.tools.length > 0) {
                                      const hasDisabledTools =
                                        mcp.disabled_tools && mcp.disabled_tools.length > 0;
                                      if (hasDisabledTools) {
                                        // Optimistic update: Update local state immediately
                                        setMcpServices((prevServices) =>
                                          prevServices.map((service) =>
                                            service.id === mcp.id
                                              ? {
                                                  ...service,
                                                  disabled_tools: [],
                                                }
                                              : service,
                                          ),
                                        );

                                        // Make API call in the background
                                        const payload = {
                                          status: mcp.status,
                                          disabled_tools: [],
                                        };
                                        try {
                                          await EditMcpChatServce(payload, mcp.id);
                                          // Refresh MCP services to sync with server (optional)
                                          const mcpResponse = await GetAllMcpChatService();
                                          if (mcpResponse.status === 200) {
                                            const mcpData = mcpResponse?.data?.data || [];
                                            setMcpServices(mcpData);
                                          }
                                        } catch (error) {
                                          // Revert optimistic update on error
                                          setMcpServices((prevServices) =>
                                            prevServices.map((service) =>
                                              service.id === mcp.id
                                                ? {
                                                    ...service,
                                                    disabled_tools: mcp.disabled_tools || [],
                                                  }
                                                : service,
                                            ),
                                          );
                                        }
                                      }
                                    }

                                    // When MCP is deselected (turned OFF), disable all its tools
                                    if (isCurrentlySelected && mcp.tools && mcp.tools.length > 0) {
                                      const hasEnabledTools =
                                        !mcp.disabled_tools ||
                                        mcp.disabled_tools.length <
                                          getMcpToolNames(mcp.tools).length;
                                      if (hasEnabledTools) {
                                        // Optimistic update: Update local state immediately
                                        setMcpServices((prevServices) =>
                                          prevServices.map((service) =>
                                            service.id === mcp.id
                                              ? {
                                                  ...service,
                                                  disabled_tools: getMcpToolNames(service.tools),
                                                }
                                              : service,
                                          ),
                                        );

                                        // Make API call in the background
                                        const payload = {
                                          status: mcp.status,
                                          disabled_tools: getMcpToolNames(mcp.tools),
                                        };
                                        try {
                                          await EditMcpChatServce(payload, mcp.id);
                                          // Refresh MCP services to sync with server (optional)
                                          const mcpResponse = await GetAllMcpChatService();
                                          if (mcpResponse.status === 200) {
                                            const mcpData = mcpResponse?.data?.data || [];
                                            setMcpServices(mcpData);
                                          }
                                        } catch (error) {
                                          // Revert optimistic update on error
                                          setMcpServices((prevServices) =>
                                            prevServices.map((service) =>
                                              service.id === mcp.id
                                                ? {
                                                    ...service,
                                                    disabled_tools: mcp.disabled_tools || [],
                                                  }
                                                : service,
                                            ),
                                          );
                                        }
                                      }
                                      // Note: We do NOT turn off MCP Access when an MCP is deselected
                                      // Deselecting an MCP should NOT affect the grandparent MCP Access toggle
                                    }
                                  }}
                                />
                                <span className="mcpSlider round" />
                              </span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="ml-6 mt-2 space-y-1">
                              {mcp?.tools?.map((toolItem) => {
                                const tool = getMcpToolName(toolItem);
                                // A tool is considered disabled if:
                                // 1. It's in the disabled_tools array, OR
                                // 2. MCP Access is off, OR
                                // 3. This MCP is not selected
                                const isToolInDisabledList = mcp.disabled_tools?.includes(tool);
                                const isMcpAccessOff = !state.config.mcpAccess;
                                const isMcpNotSelected = !state.selectedMcpIds.includes(mcp.id);
                                const isDisabled =
                                  isToolInDisabledList || isMcpAccessOff || isMcpNotSelected;
                                return (
                                  <div
                                    key={tool}
                                    className="flex items-center justify-between px-2 py-1"
                                    style={{ paddingLeft: '20px' }}
                                  >
                                    <StyledTooltip title={tool}>
                                      <span className="text-xs text-gray-600">
                                        {truncateData(tool, 35)}
                                      </span>
                                    </StyledTooltip>
                                    <div className="flex items-center gap-2">
                                      <span className="mcpSwitch">
                                        <input
                                          type="checkbox"
                                          checked={!isDisabled}
                                          onChange={() =>
                                            handleUpdateMcpTool(
                                              mcp.id,
                                              tool,
                                              mcp.disabled_tools || [],
                                              mcp.status,
                                            )
                                          }
                                          disabled={isMcpAccessOff || isMcpNotSelected}
                                        />
                                        <span className="mcpSlider round" />
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-2 py-4 text-center text-sm text-gray-500">No MCPs found</div>
                  )}
                </div>
              )}
            </div>

            {/* Code Interpreter and Web Search */}
            {[
              { key: 'codeInterpreterAccess', label: 'Code Interpreter', icon: botIcon },
              { key: 'webSearchAccess', label: 'Web Search', icon: WebSearchIcon },
            ].map(({ key, label, icon }) => (
              <div key={key} className="flex items-center justify-between px-2 py-1">
                <div className="flex items-center gap-2">
                  <img
                    src={icon}
                    alt={label}
                    className={`${label === 'Code Interpreter' ? 'w-5 h-5 -ml-1' : 'w-4 h-4'}`}
                  />
                  <span className="text-sm text-[#6A6A6A]">{label}</span>
                </div>
                <label className="switch" htmlFor={`${key}-checkbox`}>
                  <span className="sr-only text-[#6A6A6A]">{label}</span>
                  <input
                    id={`${key}-checkbox`}
                    type="checkbox"
                    checked={state.config[key as keyof ChatAgentConfig] as boolean}
                    onChange={(e) => onChange(key as keyof ChatAgentConfig, e.target.checked)}
                  />
                  <span className="slider round" />
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
      {!hideTopActionButtons &&
        showTuneModal &&
        createPortal(
          <TuneModal
            open={showTuneModal}
            reAuth={EMPTY_MCP_RE_AUTH}
            onClose={() => setShowTuneModal(false)}
            onSave={() => setShowTuneModal(false)}
            webSearchEnabled={false}
            onWebSearchToggle={() => {}}
            disabled={false}
          />,
          document.body,
        )}
    </ActionModal>
  );
}

export default ChatAgentModal;
